import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ISSUE_PRIORITIES, ISSUE_STATUSES } from "@/lib/roles";
import { fmtDate, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const EMPTY = { issue_date: todayISO(), priority: "MEDIUM", description: "", responsibility: "", area_of_concern: "", target_date: "", remarks: "" };

export default function Issues() {
  const { projectId, isProjectManager } = useProjectCtx();
  const [status, setStatus] = useState("ALL");
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/issues`, { params: status === "ALL" ? {} : { status } });
      setIssues(data);
    } finally {
      setLoading(false);
    }
  }, [projectId, status]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, next) => {
    try {
      await api.patch(`/projects/${projectId}/issues/${id}`, { status: next });
      toast.success("Issue updated");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/issues/${id}`);
      toast.success("Issue deleted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="issues-page">
      <PageHeader
        title="Issue Register"
        description="Track site, engineering and procurement issues to closure."
        action={
          <div className="flex items-center gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40" data-testid="issues-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                {ISSUE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <CreateIssueDialog projectId={projectId} onCreated={load} />
          </div>
        }
      />

      <div className="rounded-xl border border-border bg-card">
        {!loading && issues.length === 0 ? (
          <EmptyState title="No issues logged" testId="issues-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>#</TableHead><TableHead>Date</TableHead><TableHead>Priority</TableHead><TableHead>Description</TableHead><TableHead>Responsibility</TableHead><TableHead>Target</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {issues.map((i) => (
                <TableRow key={i.id} data-testid={`issue-row-${i.id}`}>
                  <TableCell className="font-mono text-xs">{i.issue_no}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.issue_date)}</TableCell>
                  <TableCell><StatusPill value={i.priority} /></TableCell>
                  <TableCell className="max-w-xs text-foreground">{i.description}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{i.responsibility || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.target_date)}</TableCell>
                  <TableCell>
                    <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                      <SelectTrigger className="h-7 w-32 text-xs" data-testid={`issue-status-select-${i.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ISSUE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    {isProjectManager && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`delete-issue-btn-${i.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete issue #{i.issue_no}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(i.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateIssueDialog({ projectId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async () => {
    try {
      await api.post(`/projects/${projectId}/issues`, { ...form, target_date: form.target_date || null });
      toast.success("Issue created");
      setForm(EMPTY);
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="create-issue-button"><Plus className="h-3.5 w-3.5" /> Log issue</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Log a new issue</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" data-testid="issue-date-input" value={form.issue_date} onChange={set("issue_date")} className="mt-1.5" /></div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="issue-priority-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{ISSUE_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea required data-testid="issue-description-input" value={form.description} onChange={set("description")} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Responsibility</Label><Input data-testid="issue-responsibility-input" value={form.responsibility} onChange={set("responsibility")} className="mt-1.5" /></div>
            <div><Label>Area of concern</Label><Input data-testid="issue-area-input" value={form.area_of_concern} onChange={set("area_of_concern")} className="mt-1.5" /></div>
          </div>
          <div><Label>Target date</Label><Input type="date" data-testid="issue-target-date-input" value={form.target_date} onChange={set("target_date")} className="mt-1.5" /></div>
          <div><Label>Remarks</Label><Textarea data-testid="issue-remarks-input" value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button disabled={!form.description} onClick={submit} data-testid="issue-submit-button">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
